import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { CalendarClock, Plus, Download, RefreshCw } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { toast } from 'sonner';

import { PageHeader } from '@/components/blocks/page-header';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useDelegationService } from '../hooks/useDelegationService';
import type { ApprovalDelegation, DelegationStatus } from '../types/delegation.types';
import { CreateDelegationDialog } from '../components/CreateDelegationDialog';
import { RevokeDelegationDialog } from '../components/RevokeDelegationDialog';
import { DelegationStatusBadge } from '../components/DelegationStatusBadge';
import { DelegationCardSkeleton } from '../components/DelegationSkeletons';
import { MyDelegationsEmptyState } from '../components/DelegationEmptyState';
import { DelegationErrorState } from '../components/DelegationErrorState';
import { DelegationPermissionGate, useDelegationPermission } from '../components/DelegationPermissionGate';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateRange(startDate: string, endDate: string): string {
  return `${format(parseISO(startDate), 'd MMM yyyy')} – ${format(parseISO(endDate), 'd MMM yyyy')}`;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const MyDelegationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canDelegate } = useDelegationPermission();
  const { service: delegationService } = useDelegationService('my-delegations');

  // Data state
  const [delegations, setDelegations] = useState<ApprovalDelegation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<DelegationStatus | 'all'>('all');

  // Dialog state — auto-open create dialog when ?create=true is present
  const [createOpen, setCreateOpen] = useState(() => searchParams.get('create') === 'true');
  const [revokeTarget, setRevokeTarget] = useState<ApprovalDelegation | null>(null);

  // ---------------------------------------------------------------------------
  // Load delegations
  // ---------------------------------------------------------------------------

  const loadDelegations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await delegationService.getMyDelegations('all');
      setDelegations(data);
    } catch {
      setError('Failed to load delegations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [delegationService]);

  useEffect(() => {
    loadDelegations();
  }, [loadDelegations]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleCreateOpenChange = useCallback((open: boolean) => {
    setCreateOpen(open);
    if (!open && searchParams.has('create')) {
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCreateSuccess = useCallback((newDelegation: ApprovalDelegation) => {
    setDelegations((prev) => [newDelegation, ...prev]);
    handleCreateOpenChange(false);
    toast.success('Delegation created', {
      description: `Delegation to ${newDelegation.delegateName} is now active.`,
    });
  }, [handleCreateOpenChange]);

  const handleRevokeSuccess = useCallback((revokedDelegation: ApprovalDelegation) => {
    setDelegations((prev) =>
      prev.map((d) => (d.id === revokedDelegation.id ? revokedDelegation : d))
    );
    setRevokeTarget(null);
    toast.success('Delegation revoked', {
      description: `The delegation to ${revokedDelegation.delegateName} has been revoked.`,
    });
  }, []);

  const handleExport = useCallback(() => {
    if (delegations.length === 0) {
      toast.error('No data to export');
      return;
    }

    const filtered = statusFilter === 'all'
      ? delegations
      : delegations.filter((d) => d.status === statusFilter);

    const rows = filtered.map((d) => ({
      Delegate: d.delegateName,
      Email: d.delegateEmail ?? '',
      'Start Date': format(parseISO(d.startDate), 'd MMM yyyy'),
      'End Date': format(parseISO(d.endDate), 'd MMM yyyy'),
      'Duration (days)': d.durationDays,
      Status: d.status,
      Notes: d.notes ?? '',
      'Revoke Reason': d.revokeReason ?? '',
    }));

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(','),
      ...rows.map((row) =>
        headers.map((h) => {
          const v = String(row[h as keyof typeof row]);
          return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
        }).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const ts = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `my-delegations-${ts}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Export downloaded', { description: `my-delegations-${ts}.csv` });
  }, [delegations, statusFilter]);

  // ---------------------------------------------------------------------------
  // Derived / filtered data
  // ---------------------------------------------------------------------------

  const filteredDelegations = useMemo(() => {
    if (statusFilter === 'all') return delegations;
    return delegations.filter((d) => d.status === statusFilter);
  }, [delegations, statusFilter]);

  const activeDelegations = useMemo(
    () => delegations.filter((d) => d.status === 'active'),
    [delegations]
  );

  // ---------------------------------------------------------------------------
  // Table columns
  // ---------------------------------------------------------------------------

  const columns: ColumnDef<ApprovalDelegation>[] = useMemo(() => [
    {
      accessorKey: 'delegateName',
      header: 'Delegate',
      cell: ({ row }) => (
        <p className="font-medium text-sm">{row.original.delegateName}</p>
      ),
      enableSorting: true,
      enableGrouping: false,
    },
    {
      id: 'dateRange',
      header: 'Date Range',
      accessorFn: (row) => row.startDate,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateRange(row.original.startDate, row.original.endDate)}
        </span>
      ),
      enableSorting: true,
      enableGrouping: false,
    },
    {
      accessorKey: 'durationDays',
      header: 'Duration',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.durationDays} day{row.original.durationDays !== 1 ? 's' : ''}
        </span>
      ),
      enableSorting: true,
      enableGrouping: false,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <DelegationStatusBadge status={row.original.status} />,
      enableSorting: true,
      enableGrouping: true,
    },
    {
      accessorKey: 'notes',
      header: 'Notes',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground line-clamp-2">
          {row.original.notes || '—'}
        </span>
      ),
      enableSorting: false,
      enableGrouping: false,
    },
    {
      accessorKey: 'revokeReason',
      header: 'Revoke Reason',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.revokeReason || '—'}
        </span>
      ),
      enableSorting: false,
      enableGrouping: false,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        row.original.status === 'active' ? (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/60 hover:bg-destructive/5"
            onClick={(e) => {
              e.stopPropagation();
              setRevokeTarget(row.original);
            }}
          >
            Revoke
          </Button>
        ) : null,
      enableSorting: false,
      enableGrouping: false,
      enableHiding: false,
    },
  ], []);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6 pb-6">
      {/* Page header */}
      <div className="flex items-start gap-4 min-w-0">
        <div className="flex-1 min-w-0">
          <PageHeader
            title="My Delegations"
            size={36}
            icon={<CalendarClock className="h-5 w-5 text-primary" />}
            showBreadcrumb={false}
            actions={
              canDelegate ? (
                <Button onClick={() => setCreateOpen(true)} size="sm" data-tour="delegation-create">
                  <Plus className="h-4 w-4 mr-2" />
                  Set Up Delegation
                </Button>
              ) : undefined
            }
          />
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <FeatureFeedbackCollector feature="Delegations" />
        </div>
      </div>

      <DelegationPermissionGate>

        {/* Error state */}
        {error && (
          <DelegationErrorState message={error} onRetry={loadDelegations} mode="alert" />
        )}

        {/* Loading state */}
        {loading && (
          <div className="space-y-3">
            <DelegationCardSkeleton />
            <DelegationCardSkeleton />
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {/* Empty state */}
            {delegations.length === 0 && (
              <MyDelegationsEmptyState onSetUp={() => setCreateOpen(true)} />
            )}

            {/* Table */}
            {delegations.length > 0 && (
              <Card data-tour="delegation-table">
                <CardContent className="pt-6">
                  <DataTableEnhanced
                    columns={columns}
                    data={filteredDelegations}
                    searchKey="delegateName"
                    searchPlaceholder="Search by delegate name..."
                    enableGrouping={false}
                    enableColumnVisibility={true}
                    enableSorting={true}
                    enableFiltering={true}
                    enableRowSelection={false}
                    pageSize={15}
                    storageKey="my-delegations"
                    fieldLabels={{
                      delegateName: 'Delegate',
                      dateRange: 'Date Range',
                      durationDays: 'Duration',
                      status: 'Status',
                      notes: 'Notes',
                      revokeReason: 'Revoke Reason',
                    }}
                    showSortIconsWhenNotSorted={false}
                    toolbarActions={
                      <>
                        <Select
                          value={statusFilter}
                          onValueChange={(v) => setStatusFilter(v as DelegationStatus | 'all')}
                        >
                          <SelectTrigger className="h-8 w-36 text-xs" data-tour="delegation-filter">
                            <SelectValue placeholder="All statuses" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                            <SelectItem value="revoked">Revoked</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadDelegations}
                          className="gap-1.5"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          Refresh
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleExport}
                          className="gap-1.5"
                          data-tour="delegation-export"
                        >
                          <Download className="h-3.5 w-3.5" />
                          Export
                        </Button>
                      </>
                    }
                  />
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Create dialog */}
        <CreateDelegationDialog
          open={createOpen}
          onOpenChange={handleCreateOpenChange}
          onSuccess={handleCreateSuccess}
          delegationService={delegationService}
        />

        {/* Revoke dialog */}
        <RevokeDelegationDialog
          delegation={revokeTarget}
          onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}
          onSuccess={handleRevokeSuccess}
          delegationService={delegationService}
        />
      </DelegationPermissionGate>
    </div>
  );
};
